// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignInButton from "@keller/App/Components/Common/SignInButton";
import ProductOriginally from "@keller/App/Components/Product/ProductOriginally";
import ProductPreviousPurchase from "@keller/App/Components/Product/ProductPreviousPurchase";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import ProductAddToCartButton from "@keller/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink from "@keller/Overrides/Components/ProductAddToListLink";
import ProductQuantityOrdered from "@keller/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    widgetId: string;
    product: ProductModel;
    extendedStyles?: ShopByProductItemStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
    };
};

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShopByProductItemStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    actionSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productOriginallyStyles?: TypographyPresentationProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceStyles?: ProductPriceStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    signInButton?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productPreviousPurchase?: TypographyPresentationProps;
}

export const shopByProductItem: ShopByProductItemStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 12px;
            width: 100%;
            height: 100%;
            ${xScreen("lte", "sm")} {
                max-width: 280px;
                flex-direction: column;
                flex-wrap: nowrap;
                width: auto;
                height: auto;
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 100px;
                height: 100px;
                margin-right: 20px;
                flex: 0 0 100px;
                ${xScreen("lte", "sm")} {
                    flex-basis: auto;
                    margin-left: auto;
                    margin-right: auto;
                    margin-bottom: 10px;
                }
            `,
        },
    },
    infoSection: {
        css: css`
            max-width: 274px;
            display: flex;
            flex-direction: column;
            flex: 1;
            ${xScreen("lte", "sm")} {
                max-width: none;
            }
        `,
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                size: 12,
                weight: "normal",
                css: css`
                    letter-spacing: 0.5px;
                    line-height: 15px;
                `,
            },
            css: css`
                margin-bottom: 4px;
                min-height: 15px;
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: 15,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 20px;
                `,
            },
            css: css`
                margin-bottom: 30px;
                min-height: 34px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            gap: 5,
            css: css`
                margin-bottom: 8px;
            `,
        },
    },
    productOriginallyStyles: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    productPriceStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                margin-bottom: 14px;
            `,
        },
        price: {
            priceText: {
                size: 18,
                weight: 900,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    margin-right: 4px;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    weight: "normal",
                    size: 12,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                    `,
                },
            },
        },
    },
    actionSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 100%;
            flex-wrap: wrap;
            justify-content: space-between;
            padding-top: 8px;
            margin-top: auto;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 100%;
            margin-bottom: 10px;
            > div:first-child {
                margin-right: 20px;
            }
        `,
    },
    productAddToCartButtonStyles: {
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
    signInButton: {
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
    productPreviousPurchase: {
        css: css`
            margin-left: auto;
            line-height: 18px;
        `,
    },
};

const ShopByProductItem: React.FC<Props> = ({ product, updateProductInfo, productInfo, extendedStyles, widgetId }) => {
    const [styles] = useState(() => mergeWithCss(shopByProductItem, extendedStyles));
    const { settingsCollection, isAuthenticated,isNonPurchasable } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        settingsCollection: getSettingsCollection(state),
        isNonPurchasable: getIsNonPurchasable(state),
    }));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                <StyledWrapper {...styles.infoSection} data-test-selector="ShopByProduct-InfoSection">
                    {productContext.product?.brand ? (
                        <ProductBrand brand={productContext.product.brand} extendedStyles={styles.productBrandStyles} />
                    ) : (
                        <StyledWrapper css={styles.productBrandStyles?.nameLink?.css} />
                    )}
                    <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                    <ProductOriginally
                        productContext={productContext}
                        extendedStyles={styles.productOriginallyStyles}
                    />
                    <ProductPrice
                        product={productContext}
                        extendedStyles={styles.productPriceStyles}
                        showSavings={false}
                        showLabel={false}
                    />
                    {settingsCollection.productSettings.showInventoryAvailability && (
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.actionSection}>
                    <StyledWrapper {...styles.addToCartSection}>
                        {isAuthenticated && productContext.product.canAddToCart ? (
                            <>
                                <ProductQuantityOrdered
                                    labelOverride=""
                                    showActions={true}
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    id={`${productContext.product.id}-qty`}
                                />
                                <ProductAddToCartButton
                                    data-test-selector={`${productContext.product.id}-addToCartBtn`}
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                />
                                {/* Xng-changes : add ProductAddToListLink for web-only with button style */}
                                {isNonPurchasable && <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                    showAsButton={isNonPurchasable}
                                />}
                            </>
                        ) : (
                            <>
                                <SignInButton extendedStyles={styles.signInButton} />
                            </>
                        )}
                    </StyledWrapper>
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                    <ProductPreviousPurchase product={product} extendedStyles={styles.productPreviousPurchase} />
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ShopByProductItem);
